import { useCallback, useRef } from 'react';
import type { WindowPosition, WindowSize } from './types';
import { MAP_TOP_OFFSET } from './clampWindowToViewport';

interface UseWindowDragOptions {
  enabled: boolean;
  position: WindowPosition;
  size: WindowSize;
  setPosition: (pos: WindowPosition) => void;
  onStart?: () => void;
}

// Constrain dragging so the entire explorer stays inside the map space.
// Header can't slide under TopMenuBar; footer can't slide off the bottom;
// sides can't slide off horizontally. (#504 follow-up)
function clampPosition(nextX: number, nextY: number, size: WindowSize): WindowPosition {
  const minX = 0;
  const maxX = Math.max(0, window.innerWidth - size.width);
  const minY = MAP_TOP_OFFSET;
  const maxY = Math.max(MAP_TOP_OFFSET, window.innerHeight - size.height);
  return {
    x: Math.max(minX, Math.min(maxX, nextX)),
    y: Math.max(minY, Math.min(maxY, nextY)),
  };
}

export function useWindowDrag({ enabled, position, size, setPosition, onStart }: UseWindowDragOptions) {
  const headerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number; panelX: number; panelY: number } | null>(null);

  // Stale-safe snapshot for listeners attached during drag
  const stateRef = useRef({ position, size });
  stateRef.current = { position, size };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!enabled) return;
    if ((e.target as HTMLElement).closest('button')) return;

    e.preventDefault();
    e.stopPropagation();
    onStart?.();

    isDraggingRef.current = true;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panelX: stateRef.current.position.x,
      panelY: stateRef.current.position.y,
    };
    document.body.classList.add('window-dragging');

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current || !dragStartRef.current) return;
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      const deltaX = moveEvent.clientX - dragStartRef.current.x;
      const deltaY = moveEvent.clientY - dragStartRef.current.y;
      setPosition(clampPosition(
        dragStartRef.current.panelX + deltaX,
        dragStartRef.current.panelY + deltaY,
        stateRef.current.size,
      ));
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      upEvent.preventDefault();
      upEvent.stopPropagation();
      isDraggingRef.current = false;
      dragStartRef.current = null;
      document.body.classList.remove('window-dragging');
      document.removeEventListener('mousemove', handleMouseMove, { capture: true });
      document.removeEventListener('mouseup', handleMouseUp, { capture: true });
    };

    document.addEventListener('mousemove', handleMouseMove, { capture: true });
    document.addEventListener('mouseup', handleMouseUp, { capture: true });
  }, [enabled, onStart, setPosition]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enabled) return;
    if ((e.target as HTMLElement).closest('button')) return;

    onStart?.();

    const touch = e.touches[0];
    isDraggingRef.current = true;
    dragStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      panelX: stateRef.current.position.x,
      panelY: stateRef.current.position.y,
    };
    document.body.classList.add('window-dragging');

    const headerEl = headerRef.current;
    if (!headerEl) return;

    const handleTouchMove = (moveEvent: TouchEvent) => {
      if (!isDraggingRef.current || !dragStartRef.current) return;
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      const t = moveEvent.touches[0];
      const deltaX = t.clientX - dragStartRef.current.x;
      const deltaY = t.clientY - dragStartRef.current.y;
      setPosition(clampPosition(
        dragStartRef.current.panelX + deltaX,
        dragStartRef.current.panelY + deltaY,
        stateRef.current.size,
      ));
    };

    const handleTouchEnd = () => {
      isDraggingRef.current = false;
      dragStartRef.current = null;
      document.body.classList.remove('window-dragging');
      headerEl.removeEventListener('touchmove', handleTouchMove);
      headerEl.removeEventListener('touchend', handleTouchEnd);
    };

    headerEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    headerEl.addEventListener('touchend', handleTouchEnd);
  }, [enabled, onStart, setPosition]);

  return { headerRef, handleMouseDown, handleTouchStart };
}
